import {
    pushElevarAddToCart,
    pushElevarRemoveFromCart,
    pushElevarLogin,
    pushElevarSelectItem,
    pushElevarSignUp,
    pushElevarUserData,
    pushElevarViewItem,
    pushElevarViewItemList,
    pushElevarViewSearchResults,
    pushElevarViewCart,
    pushElevarBeginCheckout,
    pushElevarAddShippingInfo,
    pushElevarAddPaymentInfo,
    pushElevarPurchase,
    pushElevarSubscribe,
} from './events.ts';
import { type EcomProduct, type UserAttributes, type TrackingObject } from '../types.ts';
import { eventNameConstants, stepInteractionConstants, trackingConstants } from '../constants.ts';
import { omit } from '../utils.ts';
import { compact, toStr } from './helpers.ts';
import { setElevarUserProperties } from './userProperties.ts';
import {
    ELEVAR_LIST_SEARCH_RESULTS,
    ELEVAR_LIST_IMAGE_SEARCH,
    ELEVAR_LIST_CART,
    ELEVAR_LIST_SAVE_FOR_LATER,
    type ElevarPurchaseActionField,
} from './types.ts';

const { EVENT_SIGN_UP, EVENT_LOGIN, EVENT_CART } = eventNameConstants;
const { STEP_INTERACTION_COMPLETE, STEP_INTERACTION_NEW_SOCIAL_ACCOUNT_CONNECTED } =
    stepInteractionConstants;
const { STEP_INTERACTION_REMOVE_CLICK } = stepInteractionConstants;

const SEARCH_LIST_TOKENS = [ELEVAR_LIST_SEARCH_RESULTS, ELEVAR_LIST_IMAGE_SEARCH];

const isSearchList = (list: string): boolean =>
    SEARCH_LIST_TOKENS.some(token => list.includes(token));

const firstList = (
    products?: ReadonlyArray<EcomProduct> | null,
    fallback?: string | null
): string | undefined => {
    const fromProducts = products?.find(p => !!p?.list)?.list;
    return fromProducts ?? fallback ?? undefined;
};

export const bridgeUserAttributesToElevar = (attrs: UserAttributes): void => {
    setElevarUserProperties(attrs);
    pushElevarUserData();
};

type GtmEcommerceLike = {
    type?: string;
    products?: ReadonlyArray<EcomProduct> | null;
    actionField?: {
        list?: string;
        step?: number;
    } | null;
};

export const bridgeEcommerceToElevar = (track: GtmEcommerceLike): void => {
    const { type, products, actionField } = track;
    if (!type) {
        return;
    }

    if (!products || products.length === 0) {
        return;
    }

    switch (type) {
        case trackingConstants.ECOM_PRODUCT_IMPRESSION: {
            const list = firstList(products);
            const normalizedList = list?.toLowerCase();

            if (normalizedList === ELEVAR_LIST_SAVE_FOR_LATER) {
                return;
            }

            if (normalizedList === ELEVAR_LIST_CART) {
                pushElevarViewCart({ products });
                return;
            }

            if (normalizedList && isSearchList(normalizedList)) {
                pushElevarViewSearchResults({ products, list });
            } else {
                pushElevarViewItemList({ products });
            }
            return;
        }
        case trackingConstants.ECOM_PRODUCT_CLICK: {
            const list = actionField?.list || firstList(products);
            pushElevarSelectItem({ products, list });
            return;
        }
        case trackingConstants.ECOM_PRODUCT_DETAIL: {
            const list = firstList(products, actionField?.list);
            pushElevarViewItem({ products, list });
            return;
        }
        case trackingConstants.ECOM_PRODUCT_CART: {
            const list = actionField?.list || firstList(products);
            pushElevarAddToCart({ products, list });
            return;
        }
        case trackingConstants.ECOM_PRODUCT_CHECKOUT: {
            switch (actionField?.step) {
                case 1:
                    pushElevarBeginCheckout({ products });
                    return;
                case 2:
                    pushElevarAddShippingInfo({ products });
                    return;
                case 3:
                    pushElevarAddPaymentInfo({ products });
                    return;
                default:
                    return;
            }
        }
        default:
            return;
    }
};

type GtmEventLike = {
    eventName?: string;
    step_interaction_name?: string | number;
    additional?: TrackingObject['additional'];
};

export const bridgeEventToElevar = (track: GtmEventLike): void => {
    const { additional, step_interaction_name, eventName } = track;
    // Registration sets additional.marketingOptIn when the user consents to marketing (checked the
    // opt-in box, or is auto-opted-in when not under GDPR). Login flows only include it when
    // marketingOptIn is true and the user was not already subscribed.
    if (additional?.marketingOptIn === true) {
        pushElevarSubscribe(typeof additional.email === 'string' ? additional.email : undefined);
    }

    if (
        eventName === EVENT_SIGN_UP &&
        (step_interaction_name === STEP_INTERACTION_COMPLETE ||
            step_interaction_name === STEP_INTERACTION_NEW_SOCIAL_ACCOUNT_CONNECTED)
    ) {
        pushElevarSignUp();
        return;
    }
    if (eventName === EVENT_LOGIN) {
        pushElevarLogin();
        return;
    }

    if (eventName === EVENT_CART && step_interaction_name === STEP_INTERACTION_REMOVE_CLICK) {
        if (!additional?.id) {
            return;
        }
        const list = ELEVAR_LIST_CART;
        pushElevarRemoveFromCart({
            products: [{ ...omit(additional, ['marketingOptIn', 'email']), list }],
            list,
        });
    }
};
type GtmPurchaseActionField = {
    id?: string | null;
    affiliation?: string;
    revenue?: number | string;
    tax?: number | string;
    shipping?: number | string;
    sub_total?: number | string;
    step?: number;
};

type GtmPurchaseLike = {
    products?: ReadonlyArray<EcomProduct> | null;
    actionField?: GtmPurchaseActionField | null;
};

// A purchase can be re-emitted (confirmation page remounts/revisits in the same session), which
// would double-count revenue in Elevar. Track pushed order ids so each purchase fires only once.
const pushedPurchaseIds = new Set<string>();

const mapPurchaseActionField = (
    actionField: GtmPurchaseActionField,
    id: string
): ElevarPurchaseActionField =>
    compact({
        id,
        revenue: toStr(actionField.revenue),
        tax: toStr(actionField.tax) ?? '0.0',
        shipping: toStr(actionField.shipping),
        affiliation: actionField.affiliation,
        sub_total: toStr(actionField.sub_total),
        product_sub_total: toStr(actionField.sub_total),
        sales_channel: 'website',
    });

export const bridgePurchaseToElevar = (track: GtmPurchaseLike): void => {
    const { products, actionField } = track;
    if (!products || products.length === 0 || !actionField) {
        return;
    }

    const id = actionField.id;
    if (!id) {
        return;
    }

    if (pushedPurchaseIds.has(id)) {
        return;
    }
    pushedPurchaseIds.add(id);

    pushElevarPurchase({ products, actionField: mapPurchaseActionField(actionField, id) });
};
